import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import { getEventTypeDescription, getEventTypeLabel } from "../shared/utils";
import {
  LEAD_EVENT_TYPES,
  RENTALS_TYPES,
  type LeadEventType,
  type RentalsType,
} from "../shared/types";
import type { SoftinmCatalogs, SoftinmEventMapping } from "./types";

type FormValues = {
  branchId: string;
  clientStatusId: string;
  customerStateId: string;
  clientCategoryId: string;
  priorityId: string;
};

type RentalsTypeWithSoftinmConfig = {
  id: RentalsType;
  name: string;
  crmConfiguration?: {
    id: string;
    softinmEventsMapping: SoftinmEventMapping[];
    isFallback?: boolean;
  };
};

const DEFAULT_FORM_VALUES: FormValues = {
  branchId: "",
  clientStatusId: "",
  customerStateId: "",
  clientCategoryId: "",
  priorityId: "",
};

const SoftinmCrmConfiguration = (): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);

  const { data: crmConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const {
    data: crmConfigurations,
    isLoading: isLoadingConfigurations,
    refetch: refetchConfigurations,
  } = trpc.crmSelfConfig.listCrmConfigurations.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const softinmConfigurations = useMemo(
    () => (crmConfigurations || []).filter((config) => config.provider === "softinm"),
    [crmConfigurations]
  );

  const fallbackConfiguration = useMemo(
    () => softinmConfigurations.find((config) => config.isFallback),
    [softinmConfigurations]
  );

  const rentalsTypesWithConfig = useMemo<RentalsTypeWithSoftinmConfig[]>(() => {
    if (merchantType !== "rentals") {
      return [];
    }
    return RENTALS_TYPES.map((rentalsType) => {
      const config =
        softinmConfigurations.find((c) => c.rentalsType === rentalsType.id) ||
        fallbackConfiguration;

      return {
        id: rentalsType.id,
        name: rentalsType.name,
        crmConfiguration: config
          ? {
              id: config.id,
              softinmEventsMapping: (config.softinmEventsMapping || []) as SoftinmEventMapping[],
              isFallback: !config.rentalsType && config.isFallback,
            }
          : undefined,
      };
    });
  }, [softinmConfigurations, fallbackConfiguration, merchantType]);

  const [catalogs, setCatalogs] = useState<SoftinmCatalogs | null>(null);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [eventMappings, setEventMappings] = useState<SoftinmEventMapping[]>([]);
  const [formValuesByEvent, setFormValuesByEvent] = useState<Record<string, FormValues>>({});
  const [editingRentalsType, setEditingRentalsType] = useState<RentalsType | null>(null);

  const utils = trpc.useUtils();

  const getCatalogs = trpc.crmSelfConfig.getSoftinmCatalogs.useMutation({
    onSuccess: (result) => {
      if (result.success) {
        setCatalogs(result.data);
      } else {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los catálogos",
        });
      }
      setIsLoadingCatalogs(false);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener los catálogos",
      });
      setIsLoadingCatalogs(false);
    },
  });

  const createConfiguration = trpc.crmSelfConfig.createCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración creada", {
        description: "La configuración de Softinm se guardó correctamente.",
      });
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al guardar la configuración.",
      });
    },
  });

  const updateConfiguration = trpc.crmSelfConfig.updateCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración actualizada", {
        description: "La configuración de Softinm se actualizó correctamente.",
      });
      await refetchConfigurations();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al actualizar la configuración.",
      });
    },
  });

  useEffect(() => {
    if (!editingRentalsType) {
      setEventMappings([]);
      setFormValuesByEvent({});
      return;
    }

    const config = rentalsTypesWithConfig.find(
      (rentalsType) => rentalsType.id === editingRentalsType
    )?.crmConfiguration;

    if (!config?.softinmEventsMapping) {
      setEventMappings([]);
      setFormValuesByEvent({});
      return;
    }

    const mappings = config.softinmEventsMapping as SoftinmEventMapping[];
    setEventMappings(mappings);

    const formValues: Record<string, FormValues> = {};
    mappings.forEach((mapping) => {
      if (mapping.createLead) {
        formValues[mapping.event] = {
          branchId: String(mapping.createLead.branchId ?? ""),
          clientStatusId: String(mapping.createLead.clientStatusId ?? ""),
          customerStateId: String(mapping.createLead.customerStateId ?? ""),
          clientCategoryId: String(mapping.createLead.clientCategoryId ?? ""),
          priorityId: String(mapping.createLead.priorityId ?? ""),
        };
      }
    });
    setFormValuesByEvent(formValues);
  }, [editingRentalsType, rentalsTypesWithConfig]);

  useEffect(() => {
    const apiKey = crmConnection?.softinm?.apiKey;
    const companyName = crmConnection?.softinm?.companyName;

    if (!apiKey || !companyName) {
      return;
    }

    if (catalogs || isLoadingCatalogs) {
      return;
    }

    setIsLoadingCatalogs(true);
    getCatalogs.mutate({
      apiKey,
      companyName,
    });
  }, [crmConnection, catalogs, isLoadingCatalogs, getCatalogs]);

  const getAvailableEventTypes = (): LeadEventType[] => {
    const usedEvents = new Set(eventMappings.map((m) => m.event));
    return LEAD_EVENT_TYPES.filter((event) => !usedEvents.has(event));
  };

  const handleAddEventType = (eventType: LeadEventType) => {
    setEventMappings((prev) => [
      ...prev,
      {
        event: eventType,
      },
    ]);
    setFormValuesByEvent((prev) => ({
      ...prev,
      [eventType]: { ...DEFAULT_FORM_VALUES },
    }));
  };

  const handleRemoveEventType = (eventType: LeadEventType) => {
    setEventMappings((prev) => prev.filter((m) => m.event !== eventType));
    setFormValuesByEvent((prev) => {
      const updated = { ...prev };
      delete updated[eventType];
      return updated;
    });
  };

  const handleToggleCreateLead = (eventType: LeadEventType, enabled: boolean) => {
    setEventMappings((prev) => {
      const existing = prev.find((m) => m.event === eventType);
      if (!existing) return prev;

      if (enabled) {
        return prev.map((m) =>
          m.event === eventType
            ? {
                ...m,
                createLead: {
                  branchId: 0,
                  clientStatusId: 0,
                  customerStateId: 0,
                  clientCategoryId: 0,
                  priorityId: 0,
                },
              }
            : m
        );
      } else {
        return prev.map((m) => {
          if (m.event === eventType) {
            const { createLead, ...rest } = m;
            return rest;
          }
          return m;
        });
      }
    });
  };

  const handleInputChange = (eventType: LeadEventType, field: keyof FormValues, value: string) => {
    setFormValuesByEvent((prev) => ({
      ...prev,
      [eventType]: {
        ...(prev[eventType] || DEFAULT_FORM_VALUES),
        [field]: value,
      },
    }));
  };

  const handleSelectRentalsType = (rentalsTypeId: RentalsType) => {
    setEditingRentalsType(rentalsTypeId);
  };

  const handleSave = async () => {
    if (!merchantId || !crmConnection?.provider || !editingRentalsType) {
      if (!editingRentalsType) {
        toast.warning("Selecciona un tipo de negocio", {
          description: "Elige un tipo de negocio para configurar Softinm.",
        });
      }
      return;
    }

    const softinmEventsMapping: SoftinmEventMapping[] = [];

    for (const mapping of eventMappings) {
      if (mapping.createLead) {
        const formValues = formValuesByEvent[mapping.event] || DEFAULT_FORM_VALUES;

        const requiredFields: Array<{ field: keyof FormValues; label: string }> = [
          { field: "branchId", label: "Agencia" },
          { field: "clientStatusId", label: "Estado del cliente" },
          { field: "customerStateId", label: "Estado de gestión del cliente" },
          { field: "clientCategoryId", label: "Categoría de cliente" },
          { field: "priorityId", label: "Prioridad" },
        ];

        for (const { field, label } of requiredFields) {
          if (!formValues[field]) {
            toast.error("Campos requeridos", {
              description: `Para el evento "${getEventTypeLabel(
                mapping.event
              )}", el campo "${label}" es obligatorio.`,
            });
            return;
          }
        }

        const branchItem = catalogs?.branches.find(
          (branch) => String(branch.id) === formValues.branchId
        );

        softinmEventsMapping.push({
          event: mapping.event,
          createLead: {
            branchId: Number(formValues.branchId),
            branchName: branchItem?.descripcion,
            clientStatusId: Number(formValues.clientStatusId),
            customerStateId: Number(formValues.customerStateId),
            clientCategoryId: Number(formValues.clientCategoryId),
            priorityId: Number(formValues.priorityId),
          },
        });
      } else {
        softinmEventsMapping.push({
          event: mapping.event,
        });
      }
    }

    const currentConfiguration = rentalsTypesWithConfig.find(
      (rentalsType) => rentalsType.id === editingRentalsType
    )?.crmConfiguration;

    if (currentConfiguration && !currentConfiguration.isFallback) {
      await updateConfiguration.mutateAsync({
        merchantId,
        id: currentConfiguration.id,
        softinmEventsMapping,
      });
    } else {
      await createConfiguration.mutateAsync({
        merchantId,
        provider: "softinm",
        enabled: true,
        rentalsType: editingRentalsType,
        softinmEventsMapping,
      });
    }
  };

  if (isLoadingConfigurations) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <p className="text-sm text-gray-600">
            Cargando configuración...
          </p>
        </div>
      </div>
    );
  }

  if (!crmConnection || crmConnection.provider !== "softinm") {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">Conecta Softinm para configurar los eventos.</p>
      </div>
    );
  }

  if (merchantType !== "rentals") {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">
          La configuración de Softinm está disponible para comercios de tipo arriendos.
        </p>
      </div>
    );
  }

  const availableEventTypes = getAvailableEventTypes();
  const selectedRentalsType = editingRentalsType
    ? RENTALS_TYPES.find((type) => type.id === editingRentalsType)
    : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-6">
        <div>
          <p className="mb-2 text-lg font-bold">
            Acciones Softinm
          </p>
          <p className="text-sm text-gray-600">
            Configura cómo Palomma debe crear leads en Softinm cuando se produzcan eventos.
          </p>
        </div>

        <div>
          <p className="mb-1 text-sm font-semibold">
            Configuración por Tipo de Negocio
          </p>
          <p className="mb-4 text-xs text-gray-500">
            Selecciona el tipo de negocio para el que deseas configurar el mapeo de eventos.
          </p>
          <div className="flex flex-col gap-3">
            {rentalsTypesWithConfig.map((rentalsType) => {
              const configuredEvents = rentalsType.crmConfiguration?.softinmEventsMapping || [];
              const isConfigured = configuredEvents.length > 0;
              const isSelected = editingRentalsType === rentalsType.id;
              const eventsSummary = isConfigured
                ? configuredEvents.map((m) => getEventTypeLabel(m.event)).join(", ")
                : "Sin eventos configurados";
              return (
                <div
                  key={rentalsType.id}
                  className={`rounded-md border p-4 ${
                    isSelected ? "border-purple-200 bg-purple-50/25" : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{rentalsType.name}</p>
                      <p className="text-xs text-gray-500">
                        {eventsSummary}
                      </p>
                      {rentalsType.crmConfiguration?.isFallback && (
                        <p className="text-xs text-orange-500">
                          Configuración heredada (fallback)
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          isConfigured
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-yellow-100 text-yellow-800 border-yellow-200"
                        }
                      >
                        {isConfigured ? "Configurado" : "Pendiente"}
                      </Badge>
                      <Button
                        size="sm"
                        variant={isSelected ? "default" : "outline"}
                        onClick={() => handleSelectRentalsType(rentalsType.id)}
                      >
                        {isConfigured ? "Editar" : "Configurar"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {editingRentalsType ? (
          <>
            <div>
              <p className="mb-3 text-sm font-semibold">
                Mapeo de eventos · {selectedRentalsType?.name}
              </p>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="mb-2 text-xs font-semibold text-gray-600">
                    Tipos de Eventos
                  </p>
                  {availableEventTypes.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                      {availableEventTypes.map((eventType) => (
                        <div
                          key={eventType}
                          className="rounded-md border border-gray-200 bg-gray-50 p-2"
                        >
                          <div className="flex items-center justify-between">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="flex-1 cursor-help text-xs">
                                    {getEventTypeLabel(eventType)}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {getEventTypeDescription(eventType)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-6"
                              aria-label="Agregar evento"
                              onClick={() => handleAddEventType(eventType)}
                            >
                              <Plus className="size-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs italic text-gray-500">
                      Todos los tipos de eventos han sido agregados
                    </p>
                  )}
                </div>

                {eventMappings.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-gray-600">
                      Acciones de Softinm
                    </p>
                    <div className="flex flex-col gap-3">
                      {eventMappings.map((mapping) => {
                        const hasCreateLead = !!mapping.createLead;
                        const formValues = formValuesByEvent[mapping.event] || DEFAULT_FORM_VALUES;

                        return (
                          <div
                            key={mapping.event}
                            className="rounded-md border border-gray-200 bg-gray-50 p-3"
                          >
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center justify-between">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help text-xs font-semibold text-blue-600">
                                        {getEventTypeLabel(mapping.event)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {getEventTypeDescription(mapping.event)}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-6 text-red-600 hover:text-red-700"
                                  aria-label="Eliminar evento"
                                  onClick={() => handleRemoveEventType(mapping.event)}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </div>
                              <Separator />
                              <div className="flex flex-col gap-2">
                                <div className={`flex items-center gap-2 ${hasCreateLead ? "mb-2" : ""}`}>
                                  <Checkbox
                                    checked={hasCreateLead}
                                    onCheckedChange={(checked) =>
                                      handleToggleCreateLead(mapping.event, checked as boolean)
                                    }
                                  />
                                  <label className="text-xs">Grabar Clientes</label>
                                </div>
                                {hasCreateLead && (
                                  <div className="ml-6 mt-2">
                                    {isLoadingCatalogs ? (
                                      <div className="flex items-center gap-2">
                                        <Spinner size="sm" />
                                        <p className="text-xs text-gray-500">
                                          Cargando catálogos...
                                        </p>
                                      </div>
                                    ) : catalogs ? (
                                      <div className="flex flex-col gap-2">
                                        <div className="flex flex-col gap-2">
                                          <Label className="text-xs">
                                            Agencia <span className="text-red-500">*</span>
                                          </Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            value={formValues.branchId}
                                            onChange={(e) =>
                                              handleInputChange(
                                                mapping.event,
                                                "branchId",
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">Selecciona una agencia</option>
                                            {catalogs.branches.map((branch) => (
                                              <option key={branch.id} value={branch.id}>
                                                {branch.descripcion}
                                              </option>
                                            ))}
                                          </select>
                                        </div>

                                        <div className="flex flex-col gap-2">
                                          <Label className="text-xs">
                                            Estado del cliente <span className="text-red-500">*</span>
                                          </Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            value={formValues.clientStatusId}
                                            onChange={(e) =>
                                              handleInputChange(
                                                mapping.event,
                                                "clientStatusId",
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">Selecciona un estado</option>
                                            {catalogs.clientStates.map((state) => (
                                              <option key={state.id} value={state.id}>
                                                {state.descripcion}
                                              </option>
                                            ))}
                                          </select>
                                        </div>

                                        <div className="flex flex-col gap-2">
                                          <Label className="text-xs">
                                            Estado de gestión del cliente <span className="text-red-500">*</span>
                                          </Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            value={formValues.customerStateId}
                                            onChange={(e) =>
                                              handleInputChange(
                                                mapping.event,
                                                "customerStateId",
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">Selecciona un estado</option>
                                            {catalogs.clientManagementStates.map((state) => (
                                              <option key={state.id} value={state.id}>
                                                {state.descripcion}
                                              </option>
                                            ))}
                                          </select>
                                        </div>

                                        <div className="flex flex-col gap-2">
                                          <Label className="text-xs">
                                            Categoría de cliente <span className="text-red-500">*</span>
                                          </Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            value={formValues.clientCategoryId}
                                            onChange={(e) =>
                                              handleInputChange(
                                                mapping.event,
                                                "clientCategoryId",
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">Selecciona una categoría</option>
                                            {catalogs.clientCategories.map((category) => (
                                              <option key={category.id} value={category.id}>
                                                {category.descripcion}
                                              </option>
                                            ))}
                                          </select>
                                        </div>

                                        <div className="flex flex-col gap-2">
                                          <Label className="text-xs">
                                            Prioridad <span className="text-red-500">*</span>
                                          </Label>
                                          <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            value={formValues.priorityId}
                                            onChange={(e) =>
                                              handleInputChange(
                                                mapping.event,
                                                "priorityId",
                                                e.target.value
                                              )
                                            }
                                          >
                                            <option value="">Selecciona una prioridad</option>
                                            {catalogs.clientPriorities.map((priority) => (
                                              <option key={priority.id} value={priority.id}>
                                                {priority.descripcion}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-500">
                                        Necesitas cargar los catálogos para seleccionar los campos.
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button
                onClick={handleSave}
                disabled={createConfiguration.isLoading || updateConfiguration.isLoading}
              >
                Guardar configuración
              </Button>
            </div>
          </>
        ) : (
          <div className="rounded-md border border-dashed border-gray-200 p-4">
            <p className="text-sm text-gray-600">
              Selecciona un tipo de negocio para comenzar a configurar el mapeo de eventos de
              Softinm.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoftinmCrmConfiguration;
